import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { nextUrl, oauthClient } from "@/lib/host/oauth-client";
import { orpc } from "@/lib/orpc";

export const Route = createFileRoute("/oauth/consent")({ component: OAuthConsentPage });

interface PublicClient {
  client_name?: string;
}

const LOOPBACK = new Set(["localhost", "127.0.0.1", "[::1]"]);

/** Who the app provably is. A client that identifies itself by a metadata URL (CIMD, as Claude
 * does) is vouched for by that URL's host: the document was fetched from it over TLS. A client that
 * registered itself (DCR) proves nothing, and its name and website are whatever it typed. */
function verifiedHost(clientId: string): string | null {
  if (!clientId.startsWith("https://")) return null;
  try {
    return new URL(clientId).hostname;
  } catch {
    return null;
  }
}

/** Everything shown comes from the signed authorization request and the client's record, including
 * where the browser goes next. The MCP spec requires that to be visible, with a warning when it is
 * this computer. Proposing changes can be turned off; reads cannot. */
function OAuthConsentPage() {
  const params = new URLSearchParams(typeof window === "undefined" ? "" : window.location.search);
  const clientId = params.get("client_id") ?? "";
  const requested = new Set((params.get("scope") ?? "").split(" ").filter(Boolean));
  const redirectHost = (() => {
    try {
      return new URL(params.get("redirect_uri") ?? "").hostname;
    } catch {
      return "";
    }
  })();

  const me = useQuery(orpc.auth.me.queryOptions());
  const client = useQuery({
    queryKey: ["oauth-public-client", clientId],
    enabled: clientId !== "",
    queryFn: async ({ signal }) => {
      const found = await oauthClient.$fetch<PublicClient>("/oauth2/public-client", {
        method: "GET",
        query: { client_id: clientId },
        signal,
      });
      return found.data ?? null;
    },
  });
  const [canWrite, setCanWrite] = useState(requested.has("aria:write"));
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const answer = async (accept: boolean) => {
    setPending(true);
    setError(null);
    const granted = [...requested].filter((scope) => scope !== "aria:write" || canWrite);
    try {
      const result = await oauthClient.$fetch("/oauth2/consent", {
        method: "POST",
        body: accept ? { accept, scope: granted.join(" ") } : { accept },
      });
      const url = nextUrl(result.data);
      if (url) {
        window.location.assign(url);
        return;
      }
      setError(result.error?.message ?? "This request has expired. Start again from the app.");
    } catch {
      setError("Couldn't reach the server. Check your connection and try again.");
    }
    setPending(false);
  };

  const name = client.data?.client_name ?? "An app";
  const host = verifiedHost(clientId);
  const organization = me.data?.activeOrganization?.name ?? "your organization";
  const local = LOOPBACK.has(redirectHost);

  return (
    <main className="flex min-h-dvh items-center justify-center p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="items-center text-center">
          <span className="text-lg font-semibold tracking-tight">People AI</span>
          <CardTitle asChild>
            <h1>
              {name} wants to use Aria&rsquo;s tools as {me.data?.user.name ?? "you"}
            </h1>
          </CardTitle>
          <CardDescription>In {organization}, with exactly your access.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          {host ? (
            <p className="rounded-md border p-3">
              Published by <strong>{host}</strong>, which the app&rsquo;s identity was read from.
              Only allow it if you expected {host}.
            </p>
          ) : (
            <p className="border-destructive/40 text-destructive rounded-md border p-3">
              Unverified app: it registered itself and chose the name &ldquo;{name}&rdquo;. Only
              allow it if you set it up yourself just now.
            </p>
          )}
          <ul className="list-disc space-y-1 pl-5">
            <li>
              Look up anything Aria can for you: residents, care, medications, the CRM, finances,
              staff and tasks, only in the communities you can reach.
            </li>
            <li>Stay connected until you disconnect it under Settings, Profile.</li>
          </ul>
          {requested.has("aria:write") ? (
            <div className="flex items-start gap-2 rounded-md border p-3">
              <Checkbox
                checked={canWrite}
                id="aria-write"
                onCheckedChange={(checked) => setCanWrite(checked === true)}
              />
              <Label className="leading-snug font-normal" htmlFor="aria-write">
                Also propose changes, such as a note or a task. Each one still waits for your yes
                before anything is saved.
              </Label>
            </div>
          ) : null}
          <p className="text-muted-foreground">
            You will be sent back to <strong className="text-foreground">{redirectHost}</strong>.
            {local
              ? " That is a program on this computer, such as Claude Code. Only allow it if you just started it."
              : ""}
          </p>
          {error ? (
            <p role="alert" className="text-destructive">
              {error}
            </p>
          ) : null}
          <div className="flex gap-2">
            <Button
              className="flex-1"
              disabled={pending}
              onClick={() => void answer(false)}
              variant="outline"
            >
              Deny
            </Button>
            <Button className="flex-1" disabled={pending} onClick={() => void answer(true)}>
              Allow
            </Button>
          </div>
        </CardContent>
      </Card>
    </main>
  );
}
